import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, forwardRef, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DropdownOption } from '../inputs/dropdown-grid/dropdown-grid.component';

enum TimePeriods {
  Daily = 0,
  Weekly = 2,
  Monthly = 4,
  Yearly = 5,
  Once = 101
}

enum DateShiftDirections {
  PushForward = 0,
  PullBack = 1
}

@Component({
    selector: 'app-recurring-schedule',
    templateUrl: './recurring-schedule.component.html',
    styleUrls: ['./recurring-schedule.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RecurringScheduleComponent),
            multi: true
        }
    ],
    standalone: false
})
export class RecurringScheduleComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() value: string = '0|1|-1|-1|-1|F|F|0';
  @Input() allowTimeOfDay: boolean = true;
  @Input() enableDateShifting: boolean = true;
  @Output() valueChange = new EventEmitter<string>();
  @Input() scheduleName: string = 'Schedule';
  
  isModalActive = false;
  scheduleForm: FormGroup;
  timePeriods = TimePeriods;
  dateShiftDirections = DateShiftDirections;
  weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Any'];
  months = Array.from({length: 12}, (_, i) => i + 1);
  daysOfMonth = Array.from({length: 31}, (_, i) => i + 1);
  monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December'];

  private onChange: any = () => {};
  private onTouched: any = () => {};
  disabled = false;

  isDayPickerVisible = false;
  isMonthPickerVisible = false;

  @ViewChild('dayPickerContainer') dayPickerContainer!: ElementRef;
  @ViewChild('monthPickerContainer') monthPickerContainer!: ElementRef;
  @ViewChild('weekdayPickerContainer') weekdayPickerContainer!: ElementRef;
  dropdownTop = 0;
  dropdownLeft = 0;

  isWeekdayPickerVisible = false;

  weekdayOptions = this.weekdays.slice(0, 7).map((day, index) => ({
    value: index,
    label: day
  }));

  monthOptions = this.monthNames.map((month, index) => ({
    value: index + 1,
    label: month
  }));

  constructor(private fb: FormBuilder) {
    this.scheduleForm = this.createForm();
  }

  ngOnInit() {
    if (this.value) {
      this.parseScheduleString(this.value);
    }
    // Ensure date shifting fields are cleared if disabled
    this.enforceeDateShiftingState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['enableDateShifting'] && !this.enableDateShifting) {
      // When date shifting is disabled, ensure the related options are false
      this.enforceeDateShiftingState();
    }
  }

  private enforceeDateShiftingState() {
    if (!this.enableDateShifting) {
      this.scheduleForm.patchValue({
        disallowWeekends: false,
        disallowPublicHolidays: false
      });
    }
  }

  private createForm(): FormGroup {
    return this.fb.group({
      timePeriod: [TimePeriods.Once as TimePeriods, Validators.required],
      interval: [1, [Validators.required, Validators.min(1), Validators.max(999)]],
      weekday: [7],
      dayOfMonth: [null],
      monthOfYear: [null],
      timeOfDay: [{
        value: '00:00',
        disabled: !this.allowTimeOfDay
      }],
      disallowWeekends: [false],
      disallowPublicHolidays: [false],
      dateShiftDirection: [DateShiftDirections.PushForward]
    });
  }

  private parseScheduleString(value: string) {
    const parts = value.split('|');
    if (parts.length < 9) return;

    this.scheduleForm.patchValue({
      timePeriod: parseInt(parts[0]),
      interval: parseInt(parts[1]),
      weekday: parts[2] !== '7' ? parseInt(parts[2]) : -1,
      dayOfMonth: parseInt(parts[3]),
      monthOfYear: parseInt(parts[4]),
      timeOfDay: this.minutesToTimeString(parseFloat(parts[5])),
      disallowWeekends: parts[6].toUpperCase() === 'T',
      disallowPublicHolidays: parts[7].toUpperCase() === 'T',
      dateShiftDirection: parseInt(parts[8])
    });
  }

  private minutesToTimeString(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  private timeStringToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  openModal() {
    this.isModalActive = true;
  }

  closeModal() {
    this.isModalActive = false;
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value = value;
    if (value) {
        this.parseScheduleString(value);
    } else {
        // Reset form to default values when value is null/empty
        this.scheduleForm.patchValue({
            timePeriod: TimePeriods.Once,
            interval: 1,
            weekday: -1,
            dayOfMonth: -1,
            monthOfYear: -1,
            timeOfDay: '00:00',
            disallowWeekends: false,
            disallowPublicHolidays: false,
            dateShiftDirection: DateShiftDirections.PushForward
        });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Update save method
  private generateScheduleString(formValue: any): string {
    const weekdayVal = formValue.weekday === 7 ? -1 : formValue.weekday;
    return [
        formValue.timePeriod,
        formValue.interval,
        weekdayVal,
        formValue.dayOfMonth ?? -1,
        formValue.monthOfYear ?? -1,
        this.allowTimeOfDay ? this.timeStringToMinutes(formValue.timeOfDay) : 0,
        formValue.disallowWeekends ? 'T' : 'F',
        formValue.disallowPublicHolidays ? 'T' : 'F',
        formValue.dateShiftDirection
    ].join('|');
  }

  save() {
    // Ensure date shifting values are false if disabled
    if (!this.enableDateShifting) {
      this.scheduleForm.patchValue({
        disallowWeekends: false,
        disallowPublicHolidays: false
      });
    }
    
    const scheduleString = this.generateScheduleString(this.scheduleForm.value);
    this.value = scheduleString;
    this.onChange(scheduleString);
    this.onTouched();
    this.closeModal();
  }

  getOrdinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  getScheduleSummary(): string {
    if (!this.scheduleForm) return 'No schedule set';
    
    const form = this.scheduleForm.value;
    let summary = '';
    let period: TimePeriods = Number(form.timePeriod) as TimePeriods;
    
    switch (period) {
      case TimePeriods.Once:
        summary = 'Once';
        break;
      case TimePeriods.Daily:
        summary = form.interval == 1 ? 'Daily' : `Every ${form.interval} days`;
        break;
      case TimePeriods.Weekly:
        summary = form.interval == 1 ? 'Weekly' : `Every ${form.interval} weeks`;
        if (form.weekday != -1 && form.weekday != 7) {
          summary += ` on ${this.weekdays[form.weekday]}`;
        }
        break;
      case TimePeriods.Monthly:
        summary = form.interval == 1 ? 'Monthly' : `Every ${form.interval} months`;
        if (form.dayOfMonth != -1) {
          summary += ` on the ${this.getOrdinal(form.dayOfMonth)}`;
        }
        break;
      case TimePeriods.Yearly:
        summary = form.interval == 1 ? 'Yearly' : `Every ${form.interval} years`;
        if (form.monthOfYear != -1 && form.dayOfMonth == -1) {
          summary += ` in ${this.monthNames[form.monthOfYear - 1]}`;
        }
        else if (form.monthOfYear != -1 && form.dayOfMonth != -1){
          summary += ` on ${this.monthNames[form.monthOfYear - 1]} ${this.getOrdinal(form.dayOfMonth)}`;
        }
        else if (form.dayOfMonth != -1) {
          summary += ` on the ${this.getOrdinal(form.dayOfMonth)} of the starting month`;
        }
        break;
    }

    // Time of day
    if (period != TimePeriods.Once && this.allowTimeOfDay) {
      summary += ` at ${form.timeOfDay}`;
    }

    // Restrictions (only show if date shifting is enabled)
    if (this.enableDateShifting) {
      let restrictions = [];
      if (form.disallowWeekends) restrictions.push('avoiding weekends');
      if (form.disallowPublicHolidays) restrictions.push('avoiding public holidays');
      
      if (restrictions.length > 0) {
        summary += ` (${restrictions.join(', ')}`;
        if (form.dateShiftDirection == DateShiftDirections.PushForward) {
          summary += ', moving forward as needed';
        } else {
          summary += ', moving backward as needed';
        }
        summary += ')';
      }
    }

    return summary;
  }

  getDayOfMonthDisplay(value: number): string {
    if (value === -1) return 'Any';
    return value.toString();
  }

  showDayPicker(event: FocusEvent) {
    const inputEl = event.target as HTMLElement;
    const rect = inputEl.getBoundingClientRect();
    
    // Position the dropdown below the input
    this.dropdownTop = rect.bottom + 5;
    this.dropdownLeft = rect.left;
    
    // Adjust if too close to right edge
    const dropdownWidth = 280; // Same as min-width in CSS
    if (this.dropdownLeft + dropdownWidth > window.innerWidth) {
      this.dropdownLeft = window.innerWidth - dropdownWidth - 10;
    }
    
    this.isDayPickerVisible = true;
  }

  selectDayOfMonth(day: number): void {
    this.scheduleForm.patchValue({ dayOfMonth: day });
    this.isDayPickerVisible = false;
  }

  getMonthDisplay(value: number | null): string {
    if (value === -1 || value === null) return 'Any';
    return this.monthNames[value - 1];
  }

  showMonthPicker(event: FocusEvent) {
    const inputEl = event.target as HTMLElement;
    const rect = inputEl.getBoundingClientRect();
    
    // Position the dropdown below the input
    this.dropdownTop = rect.bottom + 5;
    this.dropdownLeft = rect.left;
    
    // Adjust if too close to right edge
    const dropdownWidth = 280;
    if (this.dropdownLeft + dropdownWidth > window.innerWidth) {
      this.dropdownLeft = window.innerWidth - dropdownWidth - 10;
    }
    
    this.isMonthPickerVisible = true;
  }

  getAvailableDays(): number[] {
    const monthValue = this.scheduleForm.get('monthOfYear')?.value;
    const anyDay = -1;
    
    // Start with "Any" day option
    let days = [anyDay];
    
    // If no specific month is selected, show all possible days
    if (monthValue === null || monthValue === -1) {
      days = [...days, ...Array.from({length: 31}, (_, i) => i + 1)];
      return days;
    }

    // Get the number of days for the selected month
    let daysInMonth: number;
    if (monthValue === 2) { // February
      daysInMonth = 29; // Account for leap years by allowing 29
    } else if ([4, 6, 9, 11].includes(monthValue)) { // April, June, September, November
      daysInMonth = 30;
    } else {
      daysInMonth = 31;
    }

    // Add available days for the selected month
    days = [...days, ...Array.from({length: daysInMonth}, (_, i) => i + 1)];
    return days;
  }

  selectMonth(month: number): void {
    this.scheduleForm.patchValue({ monthOfYear: month });
    
    // Get current day selection
    const currentDay = this.scheduleForm.get('dayOfMonth')?.value;
    
    // If a specific day is selected (not -1 for "Any")
    if (currentDay > 0) {
      const availableDays = this.getAvailableDays();
      // If current day is not available in the new month, reset to "Any"
      if (!availableDays.includes(currentDay)) {
        this.scheduleForm.patchValue({ dayOfMonth: -1 });
      }
    }
    
    this.isMonthPickerVisible = false;
  }

  showWeekdayPicker(event: FocusEvent) {
    const inputEl = event.target as HTMLElement;
    const rect = inputEl.getBoundingClientRect();
    
    // Position the dropdown below the input
    this.dropdownTop = rect.bottom + 5;
    this.dropdownLeft = rect.left;
    
    // Adjust if too close to right edge
    const dropdownWidth = 280;
    if (this.dropdownLeft + dropdownWidth > window.innerWidth) {
      this.dropdownLeft = window.innerWidth - dropdownWidth - 10;
    }
    
    this.isWeekdayPickerVisible = true;
  }

  selectWeekday(day: number): void {
    this.scheduleForm.patchValue({ weekday: day });
    this.isWeekdayPickerVisible = false;
  }

  getDayOptions(): DropdownOption[] {
    return this.getAvailableDays()
      .filter(day => day !== -1)
      .map(day => ({
        value: day,
        label: this.getDayOfMonthDisplay(day)
      }));
  }
} 
